import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { DateBreakdown, maxDisabledValue } from "../../../constants";
import classes from "./BiggestChangeWidget.module.css";
import moment from "moment/moment";
import { debounce } from "lodash";
import { safeDivide } from "../../../utils/safeDivide";
import Scrollbars from "react-custom-scrollbars-2";
import { getComparingDates } from "../../../utils/getCompareDates";
import { ArrowUp } from "../../../icons/ArrowUp";
import { ArrowDown } from "../../../icons/ArrowDown";
import { useSelector } from "react-redux";

import LargestConceptMovementSummary from "./LargestConceptMovementSummary";


const LargestConceptMovement = (props) => {
    const {
        selectedDateName,
        interval,
        id,
        filters,
        conceptList,
        setFilterDocCount,
        setLocalConceptOptions,
        seletctedType,
        selectedDateField,
        selectedPageSize,
    } = props;
    const [loading, setLoading] = useState(false);
    const [conceptChanges, setConceptChanges] = useState([]);
    const [disabledIntervals, setDisabledIntervals] = useState({});
    const [comparingDates, setComparingDates] = useState([]);
    const [selectedConcept, setSelectedConcept] = useState(null);
    const [filterBaseline, setFilterBaseline] = useState(null);
    const [filterComparison, setFilterComparison] = useState(null);
    const sentiment_share_ai = useSelector(state => state.auth.flags.sentiment_share_ai);

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey] || 16;
    });
    const [getVolume] = useGetConceptsFiltersMutation();

    const conceptFilter = useMemo(
        () =>
            JSON.stringify(
                getConceptFilter({ ...props, selectedPageSize }),
            ),
        [props],
    );

    const getCount = useCallback(
        (concept) => {
            let count = concept.match_count;
            switch (seletctedType) {
                case "exact_match_count":
                    count = concept.exact_match_count;
                    break;
                case "conceptual_match_count":
                    count = concept.match_count - concept.exact_match_count;
                    break;
                default:
                    count = concept.match_count;
                    break;
            }
            return count;
        },
        [seletctedType],
    );

    const getVolumeData = useCallback(async () => {
        setLoading(true);

        const { data: dataConcepts } = await getVolume({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            breakdowns: [{ name: selectedDateName, interval }],
        });

        const breakdownData = dataConcepts.breakdowns[0].buckets;

        // set filters for summary component
        setFilterBaseline([
            {
                name: selectedDateName,
                minimum: breakdownData[breakdownData.length - 2].label,
                maximum: breakdownData[breakdownData.length - 1].label,
            },
        ]);
        setFilterComparison([
            {
                name: selectedDateName,
                minimum: breakdownData[breakdownData.length - 1].label,
            },
        ]);

        const lastData = breakdownData[breakdownData.length - 1];
        const preLastData = breakdownData[breakdownData.length - 2];
        let calculatedComparingDates;
        if (preLastData === undefined) {
            calculatedComparingDates = getComparingDates(lastData, lastData, interval);
        } else {
            calculatedComparingDates = getComparingDates(preLastData, lastData, interval);
        }
        setComparingDates(calculatedComparingDates);

        const updatedConcepts = lastData.match_counts.map((concept) => {
            const preLastConcept = preLastData?.match_counts.find(
                (c) => c.name === concept.name,
            );

            const lastCount = safeDivide(getCount(concept), lastData.total_count) * 100;
            const preLastCount = preLastConcept
                ? safeDivide(getCount(preLastConcept), preLastData.total_count) * 100
                : 0;

            let changePercentage;
            if (preLastConcept) {
                changePercentage = (lastCount - preLastCount).toFixed(1);
            } else {
                changePercentage = lastCount.toFixed(1);
            }

            return { ...concept, changePercentage, lastCount, preLastCount };
        });


        const changes = updatedConcepts.sort(
            (a, b) =>
                Math.abs(b.changePercentage) - Math.abs(a.changePercentage),
        );

        setConceptChanges(changes);
        setSelectedConcept(changes[0])
        setLoading(false);
    }, [
        getVolume,
        id,
        conceptFilter,
        filters,
        selectedDateName,
        interval,
        setFilterDocCount,
        conceptList,
        setLocalConceptOptions,
        getCount,
        selectedPageSize,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetSentiments = debounce(async () => {
            await getVolumeData();
        }, 500);

        debouncedGetSentiments();

        return () => {
            debouncedGetSentiments.cancel();
        };
    }, [getVolumeData]);

    useEffect(() => {
        const iterations = calculateIterationsForAllIntervals();
        const firstEnabledInterval =
            Object.keys(iterations).find((key) => !iterations[key].disabled) ||
            "day";
        if (!interval) setInterval(firstEnabledInterval);
        setDisabledIntervals(iterations);
    }, [selectedDateField]);


    const calculateIterationsForInterval = (minimum, maximum, interval) => {
        const startDate = moment.utc(minimum);
        const endDate = moment.utc(maximum);
        return endDate.diff(startDate, interval) + 1;
    };

    const calculateIterationsForAllIntervals = () => {
        const { minimum, maximum } = selectedDateField;
        return DateBreakdown.INTERVALS.reduce((acc, interval) => {
            const count = calculateIterationsForInterval(
                minimum,
                maximum,
                interval,
            );
            acc[interval] = { disabled: count > maxDisabledValue, count };
            return acc;
        }, {});
    };
    const height = props.widget.layout.h * 39 - 200;

    let EqualCompareDate = comparingDates[0] === comparingDates[1];

    if (loading) {
        return <Spin
            className={"spinner-by-center"}
            indicator={
                <LoadingOutlined style={{ fontSize: 24 }} spin />
            }
        />;
    }

    return (
        <div className={classes.container}>
            <div className={classes.change_type_container}>
                <div>
                    {comparingDates?.length && (
                        <div className={classes.date_indicators}>
                            <div className={classes.date_indicator}>
                                <div
                                    className={classes.bar}
                                    style={{
                                        height: "10px",
                                        width: `${props.widget.layout.w * 15}px`,
                                        background: "#04aade",
                                    }}
                                />
                                <div>{comparingDates[0]}</div>
                            </div>
                            {
                                !EqualCompareDate &&
                                <div className={classes.date_indicator}>
                                    <div
                                        className={classes.bar}
                                        style={{
                                            height: "10px",
                                            width: `${props.widget.layout.w * 15}px`,
                                            background: "#78d0ef",
                                        }}
                                    />
                                    <div>{comparingDates[1]}</div>
                                </div>
                            }

                        </div>
                    )}

                </div>
                <div className={classes.change_type}>
                    <div className={classes.gridHeader}>
                        <div className={classes.headerGridContainer}>
                            <div className={classes.gridItemHeader}>Concept</div>
                            <div className={classes.gridItemHeader}>Change</div>
                            <div className={classes.gridItemHeader}>Movement</div>
                        </div>
                    </div>
                    <Scrollbars style={{ height: height }}>
                        <div className={classes.gridContainer}>
                            {conceptChanges.map((concept, index) => (
                                <div key={concept.name + index} className={classes.gridRow}>
                                    <div style={{ fontSize: labelFontSize, cursor: "pointer" }}
                                         className={classes.gridItemConcept}
                                    >
                                        <span onMouseDown={(e) => e.stopPropagation()}
                                              onClick={() => {
                                                  setSelectedConcept(concept);
                                              }}> {concept.name}
                                        </span>
                                    </div>

                                    <div className={classes.gridItemChanges}>

                                        {!EqualCompareDate && (concept.changePercentage >= 0 ? <ArrowUp /> :
                                            <ArrowDown />)}
                                        <span style={{ fontSize: labelFontSize }}>

                                            {EqualCompareDate ? "0" : Math.abs(concept.changePercentage).toFixed(1)}%
                                            </span>
                                    </div>

                                    <div className={classes.gridItemMovement}>
                                        <div className={classes.barContainer}>
                                            <div
                                                className={classes.bar}
                                                style={{
                                                    height: "16px",
                                                    width: `${concept.lastCount}%`,
                                                    borderRadius: "0 2px 2px 0",
                                                    background: "#04aade",
                                                }}
                                                title={`${concept.lastCount.toFixed(1)} %`}
                                            />
                                            <span
                                                className={classes.barValue}>{concept.lastCount.toFixed(2)}%</span>
                                        </div>

                                        {
                                            !EqualCompareDate && <div className={classes.barContainer}>
                                                <div
                                                    className={classes.bar}
                                                    style={{
                                                        height: "16px",
                                                        width: `${concept.preLastCount}%`,
                                                        borderRadius: "0 2px 2px 0",
                                                        background: "#78d0ef",
                                                    }}
                                                    title={`${concept.preLastCount.toFixed(2)} %`}
                                                />
                                                <span
                                                    className={classes.barValue}>{concept.preLastCount.toFixed(2)}%</span>
                                            </div>
                                        }

                                    </div>
                                </div>
                            ))}
                        </div>
                        {sentiment_share_ai &&
                            <LargestConceptMovementSummary
                                comparingDates={comparingDates}
                                id={id}
                                selectedConcept={selectedConcept}
                                filterBaseline={filterBaseline}
                                filterComparison={filterComparison}
                            />
                        }
                    </Scrollbars>
                </div>
            </div>
        </div>
    );
};

export default LargestConceptMovement;
